import { useMemo, useRef, useState } from 'react';
import type { BillingImportPreview, BillingImportResult } from '@accounting/shared';
import {
  useBillingCommit,
  useBillingPreview,
  useOpenBillingInvoices,
  useReceiveBillingPayments,
} from '../../hooks/useBillingImport';
import { formatAccountingDate, formatCurrency } from '../../lib/formatters';

function errorText(err: unknown): string {
  const body = (err as { body?: { error?: string } })?.body;
  if (body?.error) return body.error;
  return (err as Error)?.message ?? 'Something went wrong.';
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Upload the advisory-fee CSV, review what it will create, then create it —
 * and settle the broker's sweep separately.
 *
 * Preview before commit is deliberate: the import posts real revenue and
 * receivables for a whole month in one action, and the file comes from a script
 * outside this system. Seeing the rows first is what makes that safe.
 */
export function BillingImportPanel({ companyId }: { companyId: string }) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<BillingImportPreview | null>(null);
  const [result, setResult] = useState<BillingImportResult | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const previewMut = useBillingPreview(companyId);
  const commitMut = useBillingCommit(companyId);

  const newRows = useMemo(
    () => (preview?.rows ?? []).filter((r) => !r.alreadyImported),
    [preview],
  );
  const blocked = (preview?.errors.length ?? 0) > 0;

  function reset() {
    setFile(null);
    setPreview(null);
    setResult(null);
    previewMut.reset();
    commitMut.reset();
    if (fileInput.current) fileInput.current.value = '';
  }

  async function onPick(f: File | null) {
    setPreview(null);
    setResult(null);
    commitMut.reset();
    setFile(f);
    if (!f) return;
    try {
      setPreview(await previewMut.mutateAsync(f));
    } catch {
      /* surfaced through previewMut.error */
    }
  }

  async function onCommit() {
    if (!file) return;
    try {
      setResult(await commitMut.mutateAsync(file));
      setPreview(null);
    } catch {
      /* surfaced through commitMut.error */
    }
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: 16, margin: 0 }}>Advisory fee billing</h2>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
            Upload the fee CSV — one invoice per row, revenue to 4009, receivable in 1008.
          </p>
        </div>
        {(preview || result) && (
          <button type="button" className="btn-secondary" onClick={reset}>
            Start over
          </button>
        )}
      </div>

      {!preview && !result && (
        <div style={{ marginTop: 12 }}>
          <input
            ref={fileInput}
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => void onPick(e.target.files?.[0] ?? null)}
          />
          <p className="muted" style={{ fontSize: 12, margin: '6px 0 0' }}>
            Columns: Name, Account Number, Invoice Date, Total.
          </p>
        </div>
      )}

      {previewMut.isPending && <p style={{ marginTop: 12 }}>Reading {file?.name}…</p>}

      {previewMut.error != null && (
        <p className="tag bad" style={{ display: 'block', marginTop: 12, padding: 8 }}>
          {errorText(previewMut.error)}
        </p>
      )}

      {preview && (
        <div style={{ marginTop: 16 }}>
          <p style={{ margin: '0 0 8px', fontSize: 13 }}>
            <strong>{preview.fileName}</strong> — {newRows.length} invoice
            {newRows.length === 1 ? '' : 's'} to create, {formatCurrency(preview.totalAmount)} total
            {preview.alreadyImportedCount > 0 && (
              <> · {preview.alreadyImportedCount} already imported and will be skipped</>
            )}
          </p>

          {blocked && (
            <div style={{ marginBottom: 12 }}>
              <p className="tag bad" style={{ display: 'block', padding: 8 }}>
                {preview.errors.length} row{preview.errors.length === 1 ? '' : 's'} cannot be
                imported. Nothing will be created until the file is fixed.
              </p>
              <ul style={{ fontSize: 13, margin: '8px 0 0', paddingLeft: 20 }}>
                {preview.errors.map((e) => (
                  <li key={`${e.rowNumber}-${e.message}`}>
                    <strong>Row {e.rowNumber}</strong>: {e.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {preview.mixedSigns && (
            <p className="tag warn" style={{ display: 'block', padding: 8, marginBottom: 12 }}>
              This file states both negative and positive totals. A fee file is
              normally negative throughout, so check the positive rows before creating —
              a reversal billed as a fee would charge the client for a credit.
            </p>
          )}

          {preview.rows.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Row</th>
                    <th style={{ textAlign: 'left' }}>Memo / description</th>
                    <th style={{ textAlign: 'left' }}>Invoice date</th>
                    <th style={{ textAlign: 'right' }}>In file</th>
                    <th style={{ textAlign: 'right' }}>Invoiced</th>
                    <th style={{ textAlign: 'left' }}>&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.externalId} style={{ opacity: r.alreadyImported ? 0.5 : 1 }}>
                      <td>{r.rowNumber}</td>
                      <td>{r.descriptor}</td>
                      <td>{formatAccountingDate(r.invoiceDate)}</td>
                      {/* Both columns, so the sign flip is visible rather than
                          something the importer did quietly. */}
                      <td className="muted" style={{ textAlign: 'right' }}>
                        {formatCurrency(r.rawTotal)}
                      </td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(r.total)}</td>
                      <td>{r.alreadyImported && <span className="tag">already imported</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {commitMut.error != null && (
            <p className="tag bad" style={{ display: 'block', marginTop: 12, padding: 8 }}>
              {errorText(commitMut.error)}
            </p>
          )}

          <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
            <button
              type="button"
              className="btn-primary"
              disabled={blocked || newRows.length === 0 || commitMut.isPending}
              onClick={() => void onCommit()}
            >
              {commitMut.isPending
                ? 'Creating…'
                : `Create ${newRows.length} invoice${newRows.length === 1 ? '' : 's'}`}
            </button>
            <span className="muted" style={{ fontSize: 12 }}>
              Revenue {preview.accounts.revenue.code} · Receivable {preview.accounts.receivable.code}{' '}
              · Deposits to {preview.accounts.deposit.code}
            </span>
          </div>
        </div>
      )}

      {result && (
        <p className="tag ok" style={{ display: 'block', marginTop: 12, padding: 8 }}>
          Created {result.created} invoice{result.created === 1 ? '' : 's'} totalling{' '}
          {formatCurrency(result.totalAmount)}
          {result.skipped > 0 && <> · skipped {result.skipped} already imported</>}.
        </p>
      )}

      <ReceivePanel companyId={companyId} />
    </div>
  );
}

/**
 * Settle the broker's monthly sweep.
 *
 * Every open invoice is listed and ticked by default, because the sweep
 * normally pays all of them — but each is a checkbox, so a partial month can be
 * settled without clearing invoices the broker has not actually paid.
 */
function ReceivePanel({ companyId }: { companyId: string }) {
  const { data: open = [], isLoading } = useOpenBillingInvoices(companyId);
  const receiveMut = useReceiveBillingPayments(companyId);
  const [paymentDate, setPaymentDate] = useState(todayIso());
  const [excluded, setExcluded] = useState<Set<string>>(new Set());

  const selected = open.filter((i) => !excluded.has(i.id));
  const selectedTotal = selected.reduce((s, i) => s + Number(i.outstandingAmount), 0);

  function toggle(id: string) {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (isLoading) return null;
  if (open.length === 0) {
    return (
      <p className="muted" style={{ fontSize: 13, marginTop: 16, marginBottom: 0 }}>
        Nothing outstanding — every advisory fee invoice has been paid.
      </p>
    );
  }

  return (
    <div style={{ marginTop: 20, borderTop: '1px solid var(--border, #ddd)', paddingTop: 16 }}>
      <h3 style={{ fontSize: 15, margin: '0 0 4px' }}>Receive payment</h3>
      <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
        {open.length} invoice{open.length === 1 ? '' : 's'} outstanding. Settling deposits to 1005.
      </p>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ width: 28 }}>&nbsp;</th>
              <th style={{ textAlign: 'left' }}>Invoice</th>
              <th style={{ textAlign: 'left' }}>Memo</th>
              <th style={{ textAlign: 'left' }}>Date</th>
              <th style={{ textAlign: 'right' }}>Outstanding</th>
            </tr>
          </thead>
          <tbody>
            {open.map((inv) => (
              <tr key={inv.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={!excluded.has(inv.id)}
                    onChange={() => toggle(inv.id)}
                    aria-label={`Include ${inv.invoiceNumber}`}
                  />
                </td>
                <td>{inv.invoiceNumber}</td>
                <td>{inv.memo}</td>
                <td>{formatAccountingDate(inv.invoiceDate)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(inv.outstandingAmount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {receiveMut.error != null && (
        <p className="tag bad" style={{ display: 'block', marginTop: 12, padding: 8 }}>
          {errorText(receiveMut.error)}
        </p>
      )}
      {receiveMut.data && (
        <p className="tag ok" style={{ display: 'block', marginTop: 12, padding: 8 }}>
          Received {formatCurrency(receiveMut.data.totalAmount)} across {receiveMut.data.paidCount}{' '}
          invoice{receiveMut.data.paidCount === 1 ? '' : 's'} into{' '}
          {receiveMut.data.depositAccountCode}.
        </p>
      )}

      <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 13 }}>
          Payment date{' '}
          <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
        </label>
        <button
          type="button"
          className="btn-primary"
          disabled={selected.length === 0 || receiveMut.isPending}
          onClick={() =>
            receiveMut.mutate({ paymentDate, invoiceIds: selected.map((i) => i.id) })
          }
        >
          {receiveMut.isPending
            ? 'Receiving…'
            : `Receive ${formatCurrency(selectedTotal.toFixed(2))} into 1005`}
        </button>
      </div>
    </div>
  );
}
